import { useCallback } from 'react';
import { useCreatePage } from '@/hooks/SpaceHooks';
import { useNullableSideBySideApi, useSideBySideIndex } from '@/components/SideBySide';
import { useSpace } from '@/components/SpaceProvider';
import { usePerformAppendBlock } from '@/components/RealtimeDocumentProvider';
import { SagaLocation, Converter, AnyBlockItem, SagaElement } from '@saga/shared';

// DoS Protection: Maximum number of top-level blocks to add
const MAX_BLOCKS = 750;

export function useCreatePageWithContent() {
    const createPage = useCreatePage();
    const performAppendBlocks = usePerformAppendBlock();
    const sideBySideApi = useNullableSideBySideApi();
    const currentPaneIndex = useSideBySideIndex();
    const { space } = useSpace();

    const createPageWithContent = useCallback(
        async (
            title: string,
            content?: string,
            options?: { showToast?: boolean; redirect?: boolean },
        ): Promise<{ success: boolean; pageId?: string; error?: string }> => {
            try {
                // Permission validation is handled by the GraphQL layer when syncing with the server.
                // The withWritingAccess middleware ensures only users with appropriate roles can create pages.
                const newPage = createPage(
                    { title },
                    { showToast: options?.showToast ?? false, redirect: options?.redirect ?? false },
                );

                // Add content if provided
                if (content) {
                    try {
                        const elements = Converter.xmlToSaga(content) as SagaElement[];
                        if (elements && elements.length > 0) {
                            // Filter out title elements as they are not AnyBlockItem
                            const blocks = elements.filter((el): el is AnyBlockItem => el.type !== 'title');

                            // DoS Protection: Limit number of blocks. If exceeded, add no content.
                            if (blocks.length > MAX_BLOCKS) {
                                console.warn(
                                    `AI content exceeds block limit (${blocks.length} > ${MAX_BLOCKS}). No content will be added.`,
                                );
                                // Silently fail to add content, but still create the page.
                                // Optionally: show a toast to the user here.
                            } else if (blocks.length > 0) {
                                await performAppendBlocks(
                                    space,
                                    { type: 'page', pageId: newPage.id, blockId: undefined },
                                    blocks,
                                );
                            }
                        }
                    } catch (error) {
                        console.error('Failed to add content to page:', error);
                        // Still open the page even if content fails
                        // Note: Not showing toast as per user preference
                    }
                }

                // Open the page in the other pane (side-by-side)
                if (sideBySideApi) {
                    const targetIndex = currentPaneIndex === 0 ? 1 : 0;
                    sideBySideApi.openLocationOnIndex(SagaLocation.pageLocationFromId(newPage.id), targetIndex);
                }

                return { success: true, pageId: newPage.id };
            } catch (error) {
                console.error('Failed to create page with content:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to create page',
                };
            }
        },
        [createPage, performAppendBlocks, sideBySideApi, currentPaneIndex, space],
    );

    return { createPageWithContent };
}
